// src/components/UserMessagesModal.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Stack,
  useMediaQuery,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import { useTheme } from "@mui/material/styles";
import { fetchUserMessages } from "../services/api";

export default function UserMessagesModal({ open, user, token, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    fetchUserMessages(user.id, token)
      .then((data) => Array.isArray(data) ? setMessages(data) : setMessages([]))
      .finally(() => setLoading(false));
  }, [open, user, token]);

  // Χρώματα/στυλ bubbles ανά αποστολέα
  const getBubbleSx = (sender) => {
    const isUser = sender === "user";
    if (isUser) {
      return {
        bgcolor: "primary.main",
        color: "primary.contrastText",
        border: "1px solid",
        borderColor: "primary.main",
      };
    }
    return {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
    };
  };

  // Wrapper για κάθε μήνυμα
  const MessageRow = ({ msg }) => {
    const isUser = msg.sender === "user";
    return (
      <Stack
        direction="row"
        spacing={1}
        justifyContent={isUser ? "flex-end" : "flex-start"}
        alignItems="flex-end"
        sx={{ width: "100%" }}
      >
        {/* Avatar αριστερά για bot, δεξιά για user */}
        {!isUser && (
          <Avatar
            sx={{ width: 30, height: 30, bgcolor: theme.palette.mode === "dark" ? "#1f2937" : "#e2e8f0" }}
          >
            <SmartToyIcon fontSize="small" />
          </Avatar>
        )}

        <Paper
          elevation={isUser ? 2 : 0}
          sx={{
            px: 1.5,
            py: 1.2,
            maxWidth: "80%",
            borderRadius: 2.5,
            borderTopLeftRadius: isUser ? 2.5 : 0.75,
            borderTopRightRadius: isUser ? 0.75 : 2.5,
            ...getBubbleSx(msg.sender),
          }}
        >
          {/* Header γραμμή: label + timestamp */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ opacity: 0.85, fontWeight: 600 }}
            >
              {isUser ? "Εσύ" : "Bot"}
            </Typography>
            {msg.timestamp && (
              <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                {msg.timestamp}
              </Typography>
            )}
          </Stack>

          {/* Κείμενο μηνύματος (κρατάει αλλαγές γραμμής, μεγάλα κείμενα σπάνε σωστά) */}
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
            }}
          >
            {msg.text}
          </Typography>
        </Paper>

        {isUser && (
          <Avatar
            sx={{ width: 30, height: 30, bgcolor: theme.palette.mode === "dark" ? "#1f2937" : "#e2e8f0" }}
          >
            <PersonIcon fontSize="small" />
          </Avatar>
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      scroll="paper"
      keepMounted
    >
      <DialogTitle
        sx={{
          pr: 6,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        Μηνύματα χρήστη #{user?.id}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: "background.default",
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: fullScreen ? "calc(100vh - 160px)" : 520,
              overflowY: "auto",
              p: { xs: 1.5, md: 2.5 },
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            {messages.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                Δεν βρέθηκαν μηνύματα.
              </Typography>
            ) : (
              <>
                {/* Επικεφαλίδα συνομιλίας (προαιρετική) */}
                <Box sx={{ px: { xs: 0.5, md: 0.5 }, pb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Συνομιλία ({messages.length})
                  </Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Box>

                {messages.map((msg) => (
                  <MessageRow key={msg.id || `${msg.timestamp}-${Math.random()}`} msg={msg} />
                ))}
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
